import type { Envio } from '@pgcustom/shared-types';
import { ApiError } from '../../core/api-client.js';
import { FORM_FIELD_CSS } from '../../core/form-field-styles.js';
import type { EnviosApi } from './envios.api.js';

/** `<erp-despachar-envio-modal>` — define transportadora/código de rastreio de um envio `criado`. Mesmo padrão de `erp-pagar-comissao-modal`. */
export class ErpDespacharEnvioModal extends HTMLElement {
  private readonly root: ShadowRoot;
  private _enviosApi: EnviosApi | null = null;
  private _aoSalvar: ((envio: Envio) => void) | null = null;

  private aberto = false;
  private envioAtual: Envio | null = null;
  private enviando = false;
  private mensagemErro = '';

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
  }

  set enviosApi(valor: EnviosApi) {
    this._enviosApi = valor;
  }

  set aoSalvar(valor: (envio: Envio) => void) {
    this._aoSalvar = valor;
  }

  abrir(envio: Envio): void {
    this.envioAtual = envio;
    this.mensagemErro = '';
    this.aberto = true;
    this.render();
  }

  connectedCallback(): void {
    this.render();
  }

  private fechar(): void {
    if (this.enviando) return;
    this.aberto = false;
    this.render();
  }

  private async aoSubmeter(evento: Event): Promise<void> {
    evento.preventDefault();
    if (!this._enviosApi || !this.envioAtual || this.enviando) return;

    const transportadora = this.valorCampo('#transportadora').trim();
    const codigoRastreio = this.valorCampo('#codigo-rastreio').trim();

    if (!transportadora) {
      this.mensagemErro = 'transportadora é obrigatória';
      this.render();
      return;
    }

    this.enviando = true;
    this.mensagemErro = '';
    this.render();

    try {
      const envio = await this._enviosApi.despachar(this.envioAtual.id, {
        transportadora,
        ...(codigoRastreio ? { codigoRastreio } : {}),
      });
      this.aberto = false;
      this._aoSalvar?.(envio);
    } catch (erro) {
      this.mensagemErro = erro instanceof ApiError ? erro.message : 'não foi possível despachar. tente novamente.';
    } finally {
      this.enviando = false;
      this.render();
    }
  }

  private valorCampo(seletor: string): string {
    return this.root.querySelector<HTMLInputElement>(seletor)?.value ?? '';
  }

  private render(): void {
    this.root.innerHTML = `
      <style>
        :host { display: block; }
        form { display: flex; flex-direction: column; gap: var(--pg-space-4, 16px); font-family: var(--pg-font-familia-base, system-ui); }
        ${FORM_FIELD_CSS}
        .rodape { display: flex; justify-content: flex-end; gap: var(--pg-space-3, 12px); }
      </style>
      <pg-modal id="modal" ${this.aberto ? 'open' : ''}>
        <span slot="header">Despachar envio</span>
        <form novalidate>
          <label>Transportadora <input id="transportadora" type="text" maxlength="100" placeholder="ex: Correios, Jadlog" /></label>
          <label>Código de rastreio (opcional) <input id="codigo-rastreio" type="text" maxlength="100" /></label>
          <span class="erro">${escaparHtml(this.mensagemErro)}</span>
        </form>
        <div slot="footer" class="rodape">
          <pg-button id="botao-cancelar" variant="ghost">Cancelar</pg-button>
          <pg-button id="botao-salvar" variant="primary" ${this.enviando ? 'loading' : ''}>Despachar</pg-button>
        </div>
      </pg-modal>
    `;

    const form = this.root.querySelector('form');
    form?.addEventListener('submit', (evento) => this.aoSubmeter(evento));
    this.root.querySelector('#botao-salvar')?.addEventListener('pg-click', (evento) => this.aoSubmeter(evento));
    this.root.querySelector('#botao-cancelar')?.addEventListener('pg-click', () => this.fechar());
    this.root.querySelector('#modal')?.addEventListener('pg-close', () => this.fechar());
  }
}

function escaparHtml(valor: string): string {
  const div = document.createElement('div');
  div.textContent = valor;
  return div.innerHTML;
}

customElements.define('erp-despachar-envio-modal', ErpDespacharEnvioModal);

declare global {
  interface HTMLElementTagNameMap {
    'erp-despachar-envio-modal': ErpDespacharEnvioModal;
  }
}
